import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2, Briefcase, UserCheck, FileText, Mail, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import SEO from '@/components/SEO';

const AdminDashboardHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeEmployers: 0,
    totalEmployers: 0,
    totalJobSeekers: 0,
    totalJobs: 0,
    pendingJobs: 0,
    approvedJobs: 0,
    totalApplications: 0,
    pendingEmployers: 0,
    totalPrimeEmployers: 0
  });
  const [enquiryStats, setEnquiryStats] = useState({ total: 0, new: 0 });
  const [recentEnquiries, setRecentEnquiries] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, countRes, enquiriesRes] = await Promise.all([
          api.get('/admin/dashboard-stats'),
          api.get('/admin/enquiries/count'),
          api.get('/admin/enquiries?limit=5')
        ]);
        
        const data = statsRes.data;
        setStats({
          activeEmployers: data.active_employers || data.employers?.approved || 0,
          totalEmployers: data.total_employers || data.employers?.total || 0,
          totalJobSeekers: data.total_job_seekers || data.job_seekers?.total || 0,
          totalJobs: data.total_jobs || data.jobs?.total || 0,
          pendingJobs: data.pending_jobs || data.jobs?.pending || 0,
          approvedJobs: data.approved_jobs || data.jobs?.active || 0,
          totalApplications: data.total_applications || data.applications?.total || 0,
          pendingEmployers: data.pending_employers || data.employers?.pending || 0,
          totalPrimeEmployers: data.total_prime_employers || data.prime_employers?.total || 0
        });
        
        setEnquiryStats(countRes.data);
        setRecentEnquiries(enquiriesRes.data.enquiries || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    
    loadData();
  }, []);

  const statCards = [
    { icon: Building2, label: 'Active Employers', value: stats.activeEmployers, color: 'bg-teal-500', link: '/admin/employers' },
    { icon: Building2, label: 'Total Employers', value: stats.totalEmployers, color: 'bg-blue-500', link: '/admin/employers' },
    { icon: UserCheck, label: 'Job Seekers', value: stats.totalJobSeekers, color: 'bg-purple-500', link: '/admin/job-seekers' },
    { icon: Briefcase, label: 'Total Jobs', value: stats.totalJobs, color: 'bg-amber-500', link: '/admin/jobs' },
    { icon: Briefcase, label: 'Pending Jobs', value: stats.pendingJobs, color: 'bg-orange-500', link: '/admin/jobs' },
    { icon: Briefcase, label: 'Approved Jobs', value: stats.approvedJobs, color: 'bg-green-500', link: '/admin/jobs' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <SEO 
        title="Dashboard | VVVSI Admin"
        description="VVVSI Admin Dashboard - Manage jobs, employers, job seekers, and enquiries."
      />
      <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>Dashboard Overview</h1>
      
      {/* Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="p-6 border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(stat.link)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-4 rounded-lg`}>
                <stat.icon className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-sm text-teal-600 hover:text-teal-700">
              <span>View Details</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </div>
          </Card>
        ))}
      </div>

      {/* Quick Actions & Enquiries */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="p-6 border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>Quick Actions</h2>
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/admin/cms')}
            >
              <FileText className="w-5 h-5 mr-3 text-teal-600" />
              Manage CMS Content
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/admin/enquiries')}
            >
              <Mail className="w-5 h-5 mr-3 text-teal-600" />
              View Enquiries
              {enquiryStats.new > 0 && (
                <span className="ml-auto bg-rose-500 text-white text-xs px-2 py-1 rounded-full">
                  {enquiryStats.new} new
                </span>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/admin/jobs')}
            >
              <Briefcase className="w-5 h-5 mr-3 text-teal-600" />
              Manage Job Posts
            </Button>
          </div>
        </Card>

        {/* Recent Enquiries */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Recent Enquiries</h2>
            <Button variant="link" onClick={() => navigate('/admin/enquiries')} className="text-teal-600">
              View All
            </Button>
          </div>
          
          {recentEnquiries.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No enquiries yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentEnquiries.map((enquiry) => (
                <div 
                  key={enquiry.id} 
                  className={`p-3 rounded-lg border ${enquiry.status === 'new' ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 border-slate-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{enquiry.name}</p>
                      <p className="text-sm text-slate-600">{enquiry.email}</p>
                    </div>
                    {enquiry.status === 'new' && (
                      <span className="text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full">New</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-1">{enquiry.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{formatDate(enquiry.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardHome;
